import * as Sentry from "@sentry/browser";
import { produce } from "immer";

import { initialAgenticAiState } from "./agenticAiState";
import type { AgenticAiAction, AgenticAiState } from "./types";

export const agenticAiReducer = (
  prevState: AgenticAiState | undefined = initialAgenticAiState,
  action: AgenticAiAction
): AgenticAiState => {
  return produce(prevState, (state) => {
    switch (action.type) {
      case "agenticAi/setSessionId":
        state.sessionId = action.sessionId;
        state.loadedSkillNames = [];
        break;
      case "agenticAi/openChat":
        state.isOpen = true;
        state.supportEntryPoint = action.withSupportEntryPoint ?? false;
        state.pendingExistingChatResponse = action.existingChatResponse;
        state.dynamicPromptsContext = action.dynamicPromptsContext;
        // Unlike dynamicPromptsContext, pending IDs are not cleared when absent:
        // they are consumed (and cleared) by the chat UI within moments, and an
        // unrelated concurrent open must not drop an in-flight attachment.
        if (action.brandGuidelineContextIds?.length) {
          state.pendingBrandGuidelineContextIds = [
            ...new Set([...(state.pendingBrandGuidelineContextIds ?? []), ...action.brandGuidelineContextIds]),
          ];
        }
        break;
      case "agenticAi/closeChat":
        state.isOpen = false;
        // Reset the support entry point flag when the chat is closed;
        // this allows users to get dynamic prompts again, or to re-enable the flag by
        // opening the chat from the support menu again.
        state.supportEntryPoint = false;
        state.pendingExistingChatResponse = undefined;
        state.dynamicPromptsContext = undefined;
        state.pendingBrandGuidelineContextIds = undefined;
        break;
      case "agenticAi/registerTool":
        if (action.key in state.frontendTools) {
          throw new Error(`Tool ${action.key} already registered`);
        }

        if (action.tool.definition.name in state.frontendToolNames) {
          const error = new Error(`Tool ${action.tool.definition.name} already registered`);
          console.error(error);
          Sentry.captureException(error, { level: "error" });
          state.frontendToolNames[action.tool.definition.name].push(action.key);
        } else {
          state.frontendToolNames[action.tool.definition.name] = [action.key];
        }

        state.frontendTools[action.key] = action.tool;
        break;
      case "agenticAi/unregisterTool":
        if (state.frontendTools[action.key]) {
          const name = state.frontendTools[action.key].definition.name;
          delete state.frontendTools[action.key];

          state.frontendToolNames[name] = state.frontendToolNames[name].filter((key) => key !== action.key);
          if (state.frontendToolNames[name].length === 0) {
            delete state.frontendToolNames[name];
          }
        }
        break;
      case "agenticAi/registerContext":
        if (action.key in state.context) {
          throw new Error(`Context ${action.key} already registered`);
        }

        if (action.identifier in state.contextIdentifiers) {
          if (!action.options.ignoreDuplicates) {
            const error = new Error(`Context with identifier "${action.identifier}" already registered`);
            console.error(error);
            Sentry.captureException(error, { level: "error" });
          }
          state.contextIdentifiers[action.identifier].push(action.key);
        } else {
          state.contextIdentifiers[action.identifier] = [action.key];
        }

        state.context[action.key] = {
          value: action.value,
          identifier: action.identifier,
          options: action.options,
        };
        break;
      case "agenticAi/unregisterContext": {
        if (state.context[action.key]) {
          const identifier = state.context[action.key].identifier;
          delete state.context[action.key];

          state.contextIdentifiers[identifier] = state.contextIdentifiers[identifier].filter(
            (key) => key !== action.key
          );
          if (state.contextIdentifiers[identifier].length === 0) {
            delete state.contextIdentifiers[identifier];
          }
        }
        break;
      }
      case "agenticAi/registerLiquidField":
        // Upsert: update if exists, insert if not
        state.liquidFields[action.key] = action.field;
        break;
      case "agenticAi/unregisterLiquidField":
        if (state.liquidFields[action.key]) {
          delete state.liquidFields[action.key];
        }
        break;
      case "agenticAi/clearSupportEntryPoint":
        state.supportEntryPoint = false;
        break;
      case "agenticAi/clearPendingExistingChatResponse":
        state.pendingExistingChatResponse = undefined;
        break;
      case "agenticAi/clearPendingBrandGuidelineContexts":
        // When the chat UI reports the specific IDs it just consumed, clear only
        // those — IDs queued by a concurrent openChat while consumption was in
        // flight must survive so they get attached on the follow-up run. With no
        // IDs (e.g. chat close, unsupported mode) drop the whole pending set.
        if (action.consumedIds?.length) {
          const consumed = new Set(action.consumedIds);
          const remaining = (state.pendingBrandGuidelineContextIds ?? []).filter((id) => !consumed.has(id));
          state.pendingBrandGuidelineContextIds = remaining.length > 0 ? remaining : undefined;
        } else {
          state.pendingBrandGuidelineContextIds = undefined;
        }
        break;
      case "agenticAi/registerSkill":
        if (action.key in state.skillDefinitions) {
          throw new Error(`Skill ${action.key} already registered`);
        }

        if (action.skill.name in state.registeredSkillNames) {
          const error = new Error(`Skill ${action.skill.name} already registered`);
          console.error(error);
          Sentry.captureException(error, { level: "error" });
          state.registeredSkillNames[action.skill.name].push(action.key);
        } else {
          state.registeredSkillNames[action.skill.name] = [action.key];
        }

        state.skillDefinitions[action.key] = action.skill;
        break;
      case "agenticAi/unregisterSkill":
        if (state.skillDefinitions[action.key]) {
          const name = state.skillDefinitions[action.key].name;
          delete state.skillDefinitions[action.key];

          state.registeredSkillNames[name] = state.registeredSkillNames[name].filter((key) => key !== action.key);
          if (state.registeredSkillNames[name].length === 0) {
            delete state.registeredSkillNames[name];
            state.loadedSkillNames = state.loadedSkillNames.filter((s) => s !== name);
          }
        }
        break;
      case "agenticAi/loadSkill":
        if (!state.loadedSkillNames.includes(action.name)) {
          state.loadedSkillNames.push(action.name);
        }
        break;
    }
  });
};
